import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, TrendingUp, TrendingDown, Download, FileSpreadsheet, Info, ChevronRight } from 'lucide-react';
import { fiscalidadAPI, gastosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Bloques del modelo 111 según las instrucciones oficiales de la AEAT.
const BLOQUES_111 = [
  { titulo: 'Rendimientos del trabajo · dinerarias', casillas: ['01', '02', '03'], principal: true,
    etiquetaImporte: 'Percepciones', etiquetaRetencion: 'Retenciones' },
  { titulo: 'Rendimientos del trabajo · en especie', casillas: ['04', '05', '06'], principal: true,
    etiquetaImporte: 'Valor percepciones en especie', etiquetaRetencion: 'Ingresos a cuenta' },
  { titulo: 'Actividades económicas · dinerarias (profesionales)', casillas: ['07', '08', '09'], principal: true,
    etiquetaImporte: 'Percepciones', etiquetaRetencion: 'Retenciones' },
  { titulo: 'Actividades económicas · en especie', casillas: ['10', '11', '12'], principal: true,
    etiquetaImporte: 'Valor percepciones en especie', etiquetaRetencion: 'Ingresos a cuenta' },
  { titulo: 'Premios de juegos, concursos y rifas · dinerarios', casillas: ['13', '14', '15'],
    etiquetaImporte: 'Importe de los premios', etiquetaRetencion: 'Retenciones' },
  { titulo: 'Premios · en especie', casillas: ['16', '17', '18'],
    etiquetaImporte: 'Valor de los premios', etiquetaRetencion: 'Ingresos a cuenta' },
  { titulo: 'Aprovechamientos forestales en montes públicos · dinerarias', casillas: ['19', '20', '21'],
    etiquetaImporte: 'Importe', etiquetaRetencion: 'Retenciones' },
  { titulo: 'Aprovechamientos forestales · en especie', casillas: ['22', '23', '24'],
    etiquetaImporte: 'Valor', etiquetaRetencion: 'Ingresos a cuenta' },
  { titulo: 'Cesión de derechos de imagen (art. 92.8)', casillas: ['25', '26', '27'],
    etiquetaImporte: 'Contraprestaciones', etiquetaRetencion: 'Ingresos a cuenta' },
];

// Clave de perceptor (se guarda en la ficha del contacto y la heredan sus facturas)
const CLAVES_111 = [
  { codigo: 'actividad_economica', etiqueta: 'Profesional (07-09)' },
  { codigo: 'trabajo', etiqueta: 'Trabajo / nómina (01-03)' },
  { codigo: 'premios', etiqueta: 'Premios (13-15)' },
  { codigo: 'forestales', etiqueta: 'Aprov. forestales (19-21)' },
  { codigo: 'imagen', etiqueta: 'Derechos de imagen (25-27)' },
  { codigo: 'alquiler_inmueble', etiqueta: 'Alquiler · modelo 115' },
];

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(Number(n || 0));

const Casilla = ({ codigo, label, valor, bold, negativo, numero, nota }) => (
  <div className={`flex items-center justify-between py-2 px-3 rounded ${bold ? 'bg-slate-100 font-semibold' : ''}`}>
    <span className="text-sm text-slate-700">
      {codigo && <span className="inline-block w-16 text-xs text-slate-400 font-mono">[{codigo}]</span>}
      {label}
      {nota && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 align-middle">{nota}</span>}
    </span>
    <span className={`text-sm tabular-nums ${negativo && valor < 0 ? 'text-red-600' : 'text-slate-900'}`}>
      {numero ? valor : eur(valor)}
    </span>
  </div>
);

const DesgloseTipos = ({ iva }) => {
  const d = iva?.desglose_tipos || {};
  const devengado = d.devengado || [];
  const exentas = d.exentas || [];
  const soportado = d.soportado || [];
  return (
    <Card data-testid="liq-desglose-tipos">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Régimen general por tipo de IVA · así se teclea en la AEAT</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="liq-tabla-tipos">
            <thead className="bg-slate-50 border-b">
              <tr className="text-slate-500">
                <th className="text-left px-3 py-2">Casilla</th>
                <th className="text-right px-3 py-2">Base imponible</th>
                <th className="text-left px-3 py-2">Casilla</th>
                <th className="text-right px-3 py-2">Tipo %</th>
                <th className="text-left px-3 py-2">Casilla</th>
                <th className="text-right px-3 py-2">Cuota</th>
                <th className="text-right px-3 py-2">Facturas</th>
              </tr>
            </thead>
            <tbody>
              {devengado.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-3 text-slate-400">Sin operaciones en régimen general este trimestre.</td></tr>
              )}
              {devengado.map((f) => (
                <tr key={f.tipo} className={`border-b last:border-0 ${f.oficial ? '' : 'bg-amber-50'}`}
                    data-testid={`liq-tipo-${f.tipo}`}>
                  <td className="px-3 py-2 font-mono text-slate-500">[{f.casilla_base || '—'}]</td>
                  <td className="px-3 py-2 text-right tabular-nums">{eur(f.base)}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">[{f.casilla_tipo || '—'}]</td>
                  <td className="px-3 py-2 text-right tabular-nums">{Number(f.tipo).toLocaleString('es-ES', { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">[{f.casilla_cuota || '—'}]</td>
                  <td className="px-3 py-2 text-right tabular-nums">{eur(f.cuota)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500">{f.facturas}</td>
                </tr>
              ))}
            </tbody>
            {devengado.length > 0 && (
              <tfoot>
                <tr className="bg-slate-100 font-semibold">
                  <td className="px-3 py-2">TOTAL</td>
                  <td className="px-3 py-2 text-right tabular-nums">{eur(devengado.reduce((a, f) => a + Number(f.base || 0), 0))}</td>
                  <td colSpan={3} className="px-3 py-2 font-mono text-slate-500">[27] cuota devengada</td>
                  <td className="px-3 py-2 text-right tabular-nums">{eur(devengado.reduce((a, f) => a + Number(f.cuota || 0), 0))}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {exentas.length > 0 && (
          <div data-testid="liq-exentas">
            <p className="text-xs font-medium text-slate-600 mb-1">Operaciones sin IVA repercutido (casillas informativas)</p>
            {exentas.map((e) => (
              <Casilla key={e.casilla} codigo={e.casilla} label={`${e.concepto} · ${e.facturas} factura(s)`} valor={e.base} />
            ))}
          </div>
        )}

        {soportado.length > 0 && (
          <div data-testid="liq-soportado-tipos">
            <p className="text-xs font-medium text-slate-600 mb-1">
              IVA soportado por tipo <span className="font-normal text-slate-400">(control interno: el 303 no desglosa las casillas 28-29)</span>
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-slate-500">
                    <th className="text-right px-3 py-2">Tipo %</th>
                    <th className="text-right px-3 py-2">Base</th>
                    <th className="text-right px-3 py-2">Base deducible</th>
                    <th className="text-right px-3 py-2">Cuota deducible</th>
                    <th className="text-right px-3 py-2">Facturas</th>
                  </tr>
                </thead>
                <tbody>
                  {soportado.map((s) => (
                    <tr key={s.tipo} className="border-b last:border-0">
                      <td className="px-3 py-2 text-right tabular-nums">{Number(s.tipo).toLocaleString('es-ES', { minimumFractionDigits: 2 })}{s.isp ? ' · ISP' : ''}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{eur(s.base)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{eur(s.base_deducible)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{eur(s.cuota)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{s.facturas}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DeducibleCasillas = ({ iva }) => {
  const filas = (iva?.desglose_tipos || {}).deducible || [];
  const total = (iva?.casillas || {})['45_total_deducir'];
  const detalle = iva?.detalle_deducible || {};
  if (filas.length === 0) return null;
  return (
    <Card data-testid="liq-deducible-casillas">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">IVA deducible · casilla a casilla (28 a 45)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="liq-tabla-deducible">
            <thead className="bg-slate-50 border-b">
              <tr className="text-slate-500">
                <th className="text-left px-3 py-2">Concepto</th>
                <th className="text-left px-3 py-2">Casilla</th>
                <th className="text-right px-3 py-2">Base</th>
                <th className="text-left px-3 py-2">Casilla</th>
                <th className="text-right px-3 py-2">Cuota</th>
                <th className="text-right px-3 py-2">Facturas</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((d) => (
                <tr key={d.clave} className={`border-b last:border-0 ${d.facturas || d.cuota ? '' : 'text-slate-400'}`}
                    data-testid={`liq-deducible-${d.casilla_cuota}`}>
                  <td className="px-3 py-2">{d.concepto}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{d.casilla_base ? `[${d.casilla_base}]` : ''}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{d.base === null ? '' : eur(d.base)}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">[{d.casilla_cuota}]</td>
                  <td className="px-3 py-2 text-right tabular-nums">{eur(d.cuota)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{d.facturas || ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-semibold">
                <td className="px-3 py-2">Total a deducir (29+31+33+35+37+39+41+42+43+44)</td>
                <td colSpan={3} className="px-3 py-2 font-mono text-slate-500">[45]</td>
                <td className="px-3 py-2 text-right tabular-nums">{eur(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        {filas.filter(d => (detalle[d.clave] || []).length > 0).map((d) => (
          <Detalle key={d.clave}
                   titulo={`${d.concepto} (casilla ${d.casilla_base ? `${d.casilla_base}/` : ''}${d.casilla_cuota})`}
                   filas={detalle[d.clave]}
                   testId={`liq-detalle-deducible-${d.casilla_cuota}`} />
        ))}
      </CardContent>
    </Card>
  );
};

const Resultado = ({ aIngresar, aCompensar, resultado, etiquetaCero }) => {
  const ingresar = Number(aIngresar || 0) > 0;
  const compensar = Number(aCompensar || 0) > 0;
  const color = ingresar ? 'red' : (compensar ? 'green' : 'slate');
  return (
    <div
      className={`rounded-lg p-4 flex items-center justify-between border ${ingresar ? 'bg-red-50 border-red-200' : (compensar ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200')}`}
      data-testid="liq-resultado"
    >
      <div className="flex items-center gap-3">
        {ingresar
          ? <TrendingUp className="w-6 h-6 text-red-600" />
          : <TrendingDown className={`w-6 h-6 ${compensar ? 'text-green-600' : 'text-slate-400'}`} />}
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500">
            {ingresar ? 'A ingresar' : (compensar ? 'A compensar' : (etiquetaCero || 'Sin resultado'))}
          </p>
          <p className={`text-2xl font-bold ${color === 'red' ? 'text-red-700' : (color === 'green' ? 'text-green-700' : 'text-slate-600')}`}>
            {eur(ingresar ? aIngresar : (compensar ? aCompensar : 0))}
          </p>
        </div>
      </div>
      <span className="text-xs text-slate-400">Resultado de la liquidación: {eur(resultado)}</span>
    </div>
  );
};

const Detalle = ({ titulo, filas, testId, isp, deducible }) => {
  const [abierto, setAbierto] = useState(false);
  const total = (filas || []).reduce((a, f) => a + Number(f.cuota || 0), 0);
  const totalBase = (filas || []).reduce((a, f) => a + Number(f.base || 0), 0);
  const totalDeducible = (filas || []).reduce((a, f) => a + Number(f.base_deducible ?? f.base ?? 0), 0);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setAbierto(!abierto)}
        className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid={testId}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <ChevronRight className={`w-4 h-4 transition-transform ${abierto ? 'rotate-90' : ''}`} />
          {titulo} ({(filas || []).length})
        </span>
        <span className="text-xs text-slate-500 tabular-nums">
          Base {eur(totalBase)}
          {deducible && <> · Deducible {eur(totalDeducible)}</>}
          {!deducible && <> · Cuota {eur(total)}</>}
        </span>
      </button>
      {abierto && (
        <div className="overflow-x-auto">
          {(filas || []).length === 0 ? (
            <p className="text-xs text-slate-400 px-3 py-3">No hay facturas en esta casilla.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-white border-b">
                <tr className="text-slate-500">
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Número</th>
                  <th className="text-left px-3 py-2">Tercero</th>
                  <th className="text-right px-3 py-2">Base</th>
                  {filas.some(f => f.afectacion !== undefined) && <th className="text-right px-3 py-2">% Afect.</th>}
                  {filas.some(f => f.base_deducible !== undefined) && <th className="text-right px-3 py-2">Base deducible</th>}
                  <th className="text-right px-3 py-2">%</th>
                  <th className="text-right px-3 py-2">{isp ? 'Cuota ISP' : 'Cuota IVA'}</th>
                  <th className="text-right px-3 py-2">Retención</th>
                  <th className="text-left px-3 py-2">Divisa</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={`${f.id}-${f.numero || ''}`} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-1.5 whitespace-nowrap">{String(f.fecha || '').slice(0, 10)}</td>
                    <td className="px-3 py-1.5">{f.numero || '—'}</td>
                    <td className="px-3 py-1.5">{f.tercero || f.descripcion || '—'}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{eur(f.base)}</td>
                    {filas.some(x => x.afectacion !== undefined) && (
                      <td className={`px-3 py-1.5 text-right tabular-nums ${Number(f.afectacion) < 100 ? 'text-amber-700 font-medium' : ''}`}>
                        {f.afectacion !== undefined ? `${Number(f.afectacion)}%` : '—'}
                      </td>
                    )}
                    {filas.some(x => x.base_deducible !== undefined) && (
                      <td className="px-3 py-1.5 text-right tabular-nums">{eur(f.base_deducible)}</td>
                    )}
                    <td className="px-3 py-1.5 text-right tabular-nums">{Number(f.porcentaje_iva || 0)}%</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{eur(f.cuota)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{eur(f.retencion)}</td>
                    <td className="px-3 py-1.5">{f.moneda && f.moneda !== 'EUR' ? `${f.moneda} ${Number(f.importe_divisa || 0).toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const PresentadasPanel = ({ ejercicio, entidadId, presentadas, onCambio, modelo = '130', descripcion }) => {
  const [edicion, setEdicion] = useState({});
  const [guardando, setGuardando] = useState(0);
  const porTrimestre = Object.fromEntries((presentadas || []).map(p => [p.trimestre, p]));

  const valor = (t, campo) => {
    if (edicion[t] && edicion[t][campo] !== undefined) return edicion[t][campo];
    const p = porTrimestre[t];
    if (!p) return '';
    if (campo === 'importe') return String(p.importe_ingresado ?? '');
    if (campo === 'justificante') return p.justificante || '';
    return p.fecha_presentacion || '';
  };

  const cambiar = (t, campo, v) => setEdicion(e => ({ ...e, [t]: { ...(e[t] || {}), [campo]: v } }));

  const guardar = async (t) => {
    const importe = parseFloat(String(valor(t, 'importe')).replace(',', '.'));
    if (Number.isNaN(importe)) {
      toast({ title: 'Indica el importe ingresado', variant: 'destructive' });
      return;
    }
    setGuardando(t);
    try {
      await fiscalidadAPI.guardarPresentada(ejercicio, {
        modelo, trimestre: t, importe_ingresado: importe,
        fecha_presentacion: valor(t, 'fecha') || null,
        justificante: valor(t, 'justificante') || null,
      }, entidadId);
      setEdicion(e => ({ ...e, [t]: undefined }));
      toast({ title: `${t}º trimestre marcado como presentado` });
      onCambio();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally { setGuardando(0); }
  };

  const quitar = async (t) => {
    setGuardando(t);
    try {
      await fiscalidadAPI.borrarPresentada(ejercicio, modelo, t, entidadId);
      setEdicion(e => ({ ...e, [t]: undefined }));
      toast({ title: `${t}º trimestre: vuelve a calcularse` });
      onCambio();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo quitar', variant: 'destructive' });
    } finally { setGuardando(0); }
  };

  return (
    <Card data-testid={`liq-presentadas-${modelo}`}>
      <CardHeader>
        <CardTitle className="text-sm">Modelos {modelo} ya presentados · {ejercicio}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-slate-500">
          {descripcion || (<>
            La casilla 05 es lo que <b>realmente ingresaste</b> en los trimestres anteriores, no un
            cálculo. Apunta aquí el resultado de cada 130 presentado y el programa lo usará tal cual.
          </>)}
        </p>
        {[1, 2, 3, 4].map(t => {
          const p = porTrimestre[t];
          return (
            <div key={t} className="flex flex-wrap items-center gap-2 py-1.5 border-b last:border-0">
              <span className="w-24 text-sm text-slate-700">{t}º Trimestre</span>
              <input
                type="text" inputMode="decimal" placeholder="Importe ingresado"
                className="w-40 h-9 px-3 rounded-md border border-slate-200 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={valor(t, 'importe')} onChange={(e) => cambiar(t, 'importe', e.target.value)}
                data-testid={`liq-presentada-${modelo}-importe-${t}`}
              />
              <input
                type="date"
                className="h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={valor(t, 'fecha')} onChange={(e) => cambiar(t, 'fecha', e.target.value)}
                data-testid={`liq-presentada-${modelo}-fecha-${t}`}
              />
              <input
                type="text" placeholder="Nº justificante"
                className="w-44 h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={valor(t, 'justificante')} onChange={(e) => cambiar(t, 'justificante', e.target.value)}
                data-testid={`liq-presentada-${modelo}-justificante-${t}`}
              />
              <Button size="sm" onClick={() => guardar(t)} disabled={guardando === t}
                      data-testid={`liq-presentada-${modelo}-guardar-${t}`}>
                {guardando === t ? <Loader2 className="w-4 h-4 animate-spin" /> : (p ? 'Actualizar' : 'Marcar presentado')}
              </Button>
              {p && (
                <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => quitar(t)}
                        disabled={guardando === t} data-testid={`liq-presentada-${modelo}-quitar-${t}`}>
                  Quitar
                </Button>
              )}
              {p && <span className="text-xs text-green-700">presentado · {eur(p.importe_ingresado)}</span>}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// `solo` limita la vista a un modelo ('111'), para poder darle su propia pestaña en Fiscalidad.
const LiquidacionesTrimestrales = ({ ejercicio, solo = null }) => {
  const [trimestre, setTrimestre] = useState(1);
  const [entidades, setEntidades] = useState([]);
  const [entidadId, setEntidadId] = useState(null);
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState('');
  const [criterio111, setCriterio111] = useState('pago');
  const [complementaria111, setComplementaria111] = useState(false);
  const [todasCasillas, setTodasCasillas] = useState(false);
  const [anual190, setAnual190] = useState(null);
  const [cargando190, setCargando190] = useState(false);

  useEffect(() => {
    gastosAPI.getFacturadoresEntidad()
      .then(({ data }) => {
        const lista = data.facturadores || [];
        setEntidades(lista);
        const eduard = lista.find(f => /eduard/i.test(f.nombre || ''));
        setEntidadId(eduard ? eduard.id : (data.default_id || (lista[0] && lista[0].id) || null));
      })
      .catch(() => setEntidades([]));
  }, []);

  const cargar = useCallback(async () => {
    if (!entidadId) return;
    setLoading(true);
    try {
      const { data } = await fiscalidadAPI.getLiquidaciones(ejercicio, trimestre, entidadId, {
        criterio111, complementaria111,
      });
      setDatos(data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron calcular las liquidaciones del trimestre', variant: 'destructive' });
      setDatos(null);
    } finally {
      setLoading(false);
    }
  }, [ejercicio, trimestre, entidadId, criterio111, complementaria111]);

  useEffect(() => { cargar(); }, [cargar]);

  const descargar = async (formato) => {
    setExportando(formato);
    try {
      const r = await fiscalidadAPI.exportLiquidaciones(ejercicio, trimestre, formato, entidadId, { criterio111 });
      const tipo = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([r.data], { type: tipo }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `liquidaciones_${ejercicio}_${trimestre}T.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast({ title: `Cuadro ${formato === 'pdf' ? 'PDF' : 'Excel'} descargado` });
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo generar el documento', variant: 'destructive' });
    } finally { setExportando(''); }
  };

  const cambiarClave = async (contactoId, clave) => {
    if (!contactoId) {
      toast({ title: 'Este perceptor no está vinculado a una ficha de contacto', variant: 'destructive' });
      return;
    }
    try {
      await fiscalidadAPI.setClavePerceptor111(contactoId, clave);
      toast({ title: 'Clave de perceptor actualizada' });
      cargar();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo guardar la clave', variant: 'destructive' });
    }
  };

  const cargar190 = async () => {
    if (anual190) { setAnual190(null); return; }
    setCargando190(true);
    try {
      const { data } = await fiscalidadAPI.getModelo190(ejercicio, entidadId, { criterio: criterio111 });
      setAnual190(data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo calcular el resumen anual (190)', variant: 'destructive' });
    } finally { setCargando190(false); }
  };

  const iva = datos?.iva_303;
  const irpf = datos?.irpf_130;
  const ret = datos?.retenciones_111;
  const c303 = iva?.casillas || {};
  const c130 = irpf?.casillas || {};
  const c111 = ret?.casillas || {};

  return (
    <div className="space-y-4" data-testid={solo === '111' ? 'retenciones-111' : 'liquidaciones-trimestrales'}>
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs text-slate-500">Entidad facturadora</Label>
              <Select value={entidadId ? String(entidadId) : undefined} onValueChange={(v) => setEntidadId(Number(v))}>
                <SelectTrigger className="w-72" data-testid="liq-entidad"><SelectValue placeholder="Selecciona entidad" /></SelectTrigger>
                <SelectContent>
                  {entidades.map(f => <SelectItem key={f.id} value={String(f.id)}>{f.nombre}{f.nif ? ` · ${f.nif}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Trimestre</Label>
              <Select value={String(trimestre)} onValueChange={(v) => setTrimestre(Number(v))}>
                <SelectTrigger className="w-36" data-testid="liq-trimestre"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4].map(t => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => descargar('excel')} disabled={!!exportando || !datos} className="bg-emerald-600 hover:bg-emerald-700" data-testid="liq-export-excel">
              {exportando === 'excel' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}Excel
            </Button>
            <Button onClick={() => descargar('pdf')} disabled={!!exportando || !datos} variant="outline" data-testid="liq-export-pdf">
              {exportando === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}PDF
            </Button>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 flex items-start gap-2" data-testid="liq-aviso-entidad">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {solo === '111'
              ? <>La entidad selecciona el titular de la declaración (nombre, NIF y régimen). El <b>modelo 111 filtra por entidad facturadora</b>; las facturas con retención que no tengan entidad asignada se incluyen y se avisa.</>
              : <>La entidad selecciona el titular de la declaración (nombre, NIF y régimen). En el 303 y el 130 los importes incluyen todavía las facturas y gastos de todo el despacho; el <b>modelo 111 sí filtra por entidad</b> (las facturas sin entidad asignada se incluyen y se avisa).</>}
          </p>
          {datos?.titular && (
            <p className="text-xs text-slate-500" data-testid="liq-titular">
              Declarante: <b>{datos.titular.nombre || '—'}</b> · NIF {datos.titular.nif || '—'} · {trimestre}T {ejercicio}
            </p>
          )}
        </CardContent>
      </Card>

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>}

      {!loading && datos && (
        <Tabs defaultValue={solo === '111' ? 'm111' : 'm303'}>
          {!solo && (
            <TabsList>
              <TabsTrigger value="m303" data-testid="liq-tab-303">Modelo 303 · IVA</TabsTrigger>
              <TabsTrigger value="m130" data-testid="liq-tab-130">Modelo 130 · IRPF</TabsTrigger>
              <TabsTrigger value="m111" data-testid="liq-tab-111">Modelo 111 · Retenciones</TabsTrigger>
            </TabsList>
          )}

          <TabsContent value="m303" className="mt-4 space-y-4">
            <Resultado aIngresar={iva?.a_ingresar} aCompensar={iva?.a_compensar} resultado={iva?.resultado} />
            <Card>
              <CardHeader><CardTitle className="text-sm">Casillas del Modelo 303 · {trimestre}T {ejercicio}</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                <Casilla codigo="01-09" label="Base imponible devengada (suma del desglose por tipo)" valor={c303.base_devengada} />
                <Casilla codigo="03-09" label="Cuota devengada en operaciones interiores (suma del desglose)" valor={c303.cuota_devengada} />
                <Casilla codigo="10" label="Adquisiciones intracomunitarias de bienes y servicios · base" valor={c303['10_intracomunitarias_base']} />
                <Casilla codigo="11" label="Adquisiciones intracomunitarias · cuota autorrepercutida" valor={c303['11_intracomunitarias_cuota']} />
                <Casilla codigo="12" label="Otras operaciones con inversión del sujeto pasivo · base (no UE)" valor={c303['12_isp_base']} />
                <Casilla codigo="13" label="Otras operaciones con inversión del sujeto pasivo · cuota" valor={c303['13_isp_cuota']} />
                <Casilla codigo="27" label="Total cuota devengada" valor={c303['27_total_devengado']} bold />
                <Casilla codigo="45" label="Total a deducir (ver el desglose de abajo)" valor={c303['45_total_deducir']} bold />
                <Casilla codigo="46" label="Resultado del régimen general" valor={c303['46_resultado_regimen']} negativo />
                <Casilla codigo="78" label="Cuotas a compensar de periodos anteriores" valor={c303['78_cuotas_compensar_previas']} />
                <Casilla codigo="71" label="Resultado de la liquidación" valor={c303['71_resultado']} bold negativo />
              </CardContent>
            </Card>

            <DesgloseTipos iva={iva} />

            <DeducibleCasillas iva={iva} />

            {(iva?.avisos_303 || []).length > 0 && (
              <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1" data-testid="liq-avisos-303">
                {(iva.avisos_303 || []).map((a, i) => (
                  <p key={i} className="text-xs text-amber-900 flex gap-2"><Info className="w-3 h-3 mt-0.5 shrink-0" />{a}</p>
                ))}
              </div>
            )}

            {trimestre === 4 && iva?.regularizacion_inversiones && (
              <Card data-testid="liq-regularizacion-4t">
                <CardHeader><CardTitle className="text-sm">Regularización de bienes de inversión · {eur(iva.regularizacion_inversiones.total)}</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {(iva.regularizacion_inversiones.detalle || []).map((d, i) => (
                    <div key={i} className="flex justify-between text-xs py-1 border-b last:border-0">
                      <span>{d.descripcion || `Bien ${d.bien_id || i + 1}`}{d.periodo ? ` · ${d.periodo}` : ''}</span>
                      <span className="tabular-nums">{eur(d.ajuste)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              <p className="text-xs text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" /> Detalle factura a factura de cada casilla, para cuadrar la liquidación.</p>
              <Detalle titulo="Facturas emitidas (casillas 01-09)" filas={iva?.detalle?.devengado} testId="liq-detalle-devengado" />
              {(iva?.detalle?.exentas || []).length > 0 && (
                <Detalle titulo="Facturas emitidas sin IVA repercutido (casillas 59/60/61)" filas={iva?.detalle?.exentas} testId="liq-detalle-exentas" />
              )}
              <Detalle titulo="Gastos con IVA deducible (casillas 28-29)" filas={iva?.detalle?.soportado} testId="liq-detalle-soportado" />
              <Detalle titulo="Inversión del sujeto pasivo · fuera de la UE (casillas 12-13)" filas={iva?.detalle?.isp} testId="liq-detalle-isp" isp />
              {(iva?.detalle?.intracomunitarias || []).length > 0 && (
                <Detalle titulo="Adquisiciones intracomunitarias (casillas 10-11 y 36-37)" filas={iva?.detalle?.intracomunitarias} testId="liq-detalle-intracomunitarias" isp />
              )}
              <Detalle titulo="Gastos sin IVA deducible (no computan)" filas={iva?.detalle?.no_deducible} testId="liq-detalle-no-deducible" />
            </div>
          </TabsContent>

          <TabsContent value="m130" className="mt-4 space-y-4">
            {irpf?.exonerado_130 && (
              <div className="rounded bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="liq-exonerado-130">
                El {irpf.pct_ingresos_con_retencion}% de los ingresos llevan retención (≥70%): podrías estar <b>exonerado</b> de presentar el Modelo 130.
              </div>
            )}
            <Resultado aIngresar={irpf?.a_ingresar} aCompensar={irpf?.negativo_arrastrar} resultado={irpf?.resultado} etiquetaCero="Sin cuota a ingresar" />
            <Card>
              <CardHeader><CardTitle className="text-sm">Casillas del Modelo 130 · acumulado a {trimestre}T {ejercicio}</CardTitle></CardHeader>
              <CardContent className="space-y-1">
                <Casilla codigo="01" label="Ingresos íntegros" valor={c130['01_ingresos']} />
                <Casilla codigo="02" label="Gastos deducibles (incluye difícil justificación)" valor={c130['02_gastos']} />
                <Casilla label="  · gastos justificados" valor={c130['02_gastos_deducibles']} />
                <Casilla label="  · amortizaciones" valor={c130['02_amortizaciones']} />
                <Casilla label="  · difícil justificación" valor={c130['02_dificil_justificacion']} />
                <Casilla codigo="03" label="Rendimiento neto" valor={c130['03_rendimiento_neto']} bold negativo />
                <Casilla codigo="04" label="20% sobre el rendimiento neto" valor={c130['04_20_por_ciento']} bold />
                <Casilla codigo="05" label="Pagos fraccionados de trimestres anteriores" valor={c130['05_pagos_previos']}
                         nota={{ presentado: 'según 130 presentados', manual: 'introducido a mano', calculado: 'calculado' }[c130['05_origen']]} />
                <Casilla codigo="06" label="Retenciones soportadas" valor={c130['06_retenciones']} />
                <Casilla codigo="13" label="Minoración por rendimientos bajos" valor={c130['13_minoracion']} />
                <Casilla codigo="07" label="Resultado" valor={c130['07_resultado']} bold negativo />
              </CardContent>
            </Card>
            <PresentadasPanel ejercicio={ejercicio} entidadId={entidadId}
                              presentadas={datos?.presentadas?.['130'] || []} onCambio={cargar} />
            {(irpf?.pagos_previos_detalle || []).length > 0 && (
              <Card data-testid="liq-pagos-previos">
                <CardHeader><CardTitle className="text-sm">Desglose de la casilla 05</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {irpf.pagos_previos_detalle.map(d => (
                    <div key={d.trimestre} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
                      <span>
                        {d.trimestre}º Trimestre
                        <span className={`ml-2 px-1.5 py-0.5 rounded ${d.origen === 'presentado' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {d.origen === 'presentado' ? `presentado${d.fecha_presentacion ? ` el ${d.fecha_presentacion}` : ''}` : 'calculado con los datos de hoy'}
                        </span>
                      </span>
                      <span className="tabular-nums">{eur(d.importe)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            <div className="space-y-2">
              <Detalle titulo="Ingresos acumulados del ejercicio (casilla 01)" filas={irpf?.detalle?.ingresos} testId="liq-detalle-ingresos" />
              <Detalle titulo="Gastos deducibles acumulados (casilla 02)" filas={irpf?.detalle?.gastos} testId="liq-detalle-gastos" deducible />
            </div>
          </TabsContent>

          <TabsContent value="m111" className="mt-4 space-y-4">
            {ret?.sin_retenciones ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600" data-testid="liq-111-sin-retenciones">
                <b>Sin retenciones en el periodo.</b> No hay que presentar el modelo 111 del {trimestre}T {ejercicio}.
              </div>
            ) : (
              <Resultado aIngresar={ret?.a_ingresar} resultado={ret?.resultado} etiquetaCero="Sin retenciones que ingresar" />
            )}

            <Card>
              <CardContent className="pt-4 flex flex-wrap items-end gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Criterio de imputación</Label>
                  <Select value={criterio111} onValueChange={setCriterio111}>
                    <SelectTrigger className="w-72" data-testid="liq-111-criterio"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pago">Fecha de pago (si falta, la de factura)</SelectItem>
                      <SelectItem value="factura">Fecha de factura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!!ret?.presentada && (
                  <label className="flex items-center gap-2 text-xs text-slate-600 h-9" data-testid="liq-111-complementaria-label">
                    <input type="checkbox" checked={complementaria111}
                           onChange={(e) => setComplementaria111(e.target.checked)}
                           data-testid="liq-111-complementaria" />
                    Declaración complementaria (lleva lo presentado a la casilla 29)
                  </label>
                )}
                <Button variant="ghost" size="sm" className="text-slate-500"
                        onClick={() => setTodasCasillas(!todasCasillas)} data-testid="liq-111-ver-todas">
                  {todasCasillas ? 'Ver solo las casillas con datos' : 'Ver las 30 casillas'}
                </Button>
              </CardContent>
            </Card>

            {(ret?.avisos || []).length > 0 && (
              <div className="space-y-1" data-testid="liq-111-avisos">
                {ret.avisos.map((a, i) => (
                  <p key={i} className={`text-xs rounded px-3 py-2 border flex items-start gap-2 ${
                    a.tipo === 'arrendamiento_115' || a.tipo === 'complementaria_sugerida'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                     data-testid={`liq-111-aviso-${a.tipo}`}>
                    <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{a.mensaje}
                  </p>
                ))}
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">
                  Casillas del Modelo 111 · {trimestre}T {ejercicio}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {BLOQUES_111.filter(b => todasCasillas || b.principal
                        || Number(c111[`${b.casillas[2]}_retenciones`] || 0) !== 0
                        || Number(c111[`${b.casillas[0]}_num_perceptores`] || 0) !== 0)
                  .map(b => (
                    <div key={b.casillas[0]} className="pt-1">
                      <p className="text-[11px] uppercase tracking-wide text-slate-400 px-3">{b.titulo}</p>
                      <Casilla codigo={b.casillas[0]} label="Nº de perceptores" numero
                               valor={c111[`${b.casillas[0]}_num_perceptores`] || 0} />
                      <Casilla codigo={b.casillas[1]} label={b.etiquetaImporte}
                               valor={c111[`${b.casillas[1]}_percepciones`] || 0} />
                      <Casilla codigo={b.casillas[2]} label={b.etiquetaRetencion}
                               valor={c111[`${b.casillas[2]}_retenciones`] || 0}
                               bold={b.casillas[2] === '09'} />
                    </div>
                  ))}
                <div className="pt-2 border-t mt-2">
                  <Casilla codigo="28" label="Suma de retenciones e ingresos a cuenta" valor={c111['28_total_retenciones']} bold />
                  <Casilla codigo="29" label="Resultados a ingresar de declaraciones anteriores del mismo periodo" valor={c111['29_resultados_anteriores']} />
                  <Casilla codigo="30" label="Resultado a ingresar" valor={c111['30_resultado']} bold />
                </div>
              </CardContent>
            </Card>

            {(ret?.perceptores || []).length > 0 && (
              <Card data-testid="liq-perceptores">
                <CardHeader><CardTitle className="text-sm">Perceptores del trimestre</CardTitle></CardHeader>
                <CardContent className="overflow-x-auto p-0">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-slate-500">
                        <th className="text-left px-3 py-2">NIF</th>
                        <th className="text-left px-3 py-2">Perceptor</th>
                        <th className="text-left px-3 py-2">Clave</th>
                        <th className="text-left px-3 py-2">Casillas</th>
                        <th className="text-right px-3 py-2">Facturas</th>
                        <th className="text-right px-3 py-2">% ret.</th>
                        <th className="text-right px-3 py-2">Percepciones</th>
                        <th className="text-right px-3 py-2">Retenciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ret.perceptores.map((p, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-slate-50" data-testid={`liq-perceptor-${i}`}>
                          <td className={`px-3 py-1.5 font-mono ${p.nif ? '' : 'text-red-600'}`}>{p.nif || 'SIN NIF'}</td>
                          <td className="px-3 py-1.5">{p.nombre || p.proveedor || '—'}</td>
                          <td className="px-3 py-1.5">
                            <Select value={p.clave_111} onValueChange={(v) => cambiarClave(p.proveedor_id, v)}>
                              <SelectTrigger className="h-7 w-48 text-xs" data-testid={`liq-perceptor-clave-${i}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CLAVES_111.map(c => <SelectItem key={c.codigo} value={c.codigo}>{c.etiqueta}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <span className="block text-[10px] text-slate-400 mt-0.5">190: {p.clave_190}.{p.subclave_190}</span>
                          </td>
                          <td className="px-3 py-1.5 font-mono text-slate-400">{(p.casillas || []).join('-')}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{p.facturas}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{(p.porcentajes || []).map(x => `${x}%`).join(', ') || '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{eur(p.percepciones)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{eur(p.retenciones)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {(ret?.excluidos_115 || []).length > 0 && (
              <Card data-testid="liq-111-excluidos-115">
                <CardHeader>
                  <CardTitle className="text-sm text-red-700">
                    Excluidos del 111 · corresponden al Modelo 115 (arrendamientos)
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto p-0">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b">
                      <tr className="text-slate-500">
                        <th className="text-left px-3 py-2">Fecha</th>
                        <th className="text-left px-3 py-2">Número</th>
                        <th className="text-left px-3 py-2">Perceptor</th>
                        <th className="text-right px-3 py-2">Base</th>
                        <th className="text-right px-3 py-2">Retención</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ret.excluidos_115.map((e, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-1.5">{String(e.fecha || '').slice(0, 10)}</td>
                          <td className="px-3 py-1.5">{e.numero || '—'}</td>
                          <td className="px-3 py-1.5">{e.perceptor || '—'}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{eur(e.base)}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{eur(e.retencion)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            <PresentadasPanel ejercicio={ejercicio} entidadId={entidadId} modelo="111"
                              presentadas={datos?.presentadas?.['111'] || []} onCambio={cargar}
                              descripcion={<>Apunta aquí cada <b>modelo 111 presentado</b>. Si el cálculo de hoy no coincide con lo presentado, te lo avisará y podrás preparar una complementaria.</>} />

            <Card data-testid="liq-190">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm">Resumen anual · Modelo 190 · {ejercicio}</CardTitle>
                <Button size="sm" variant="outline" onClick={cargar190} disabled={cargando190} data-testid="liq-190-cargar">
                  {cargando190 ? <Loader2 className="w-4 h-4 animate-spin" /> : (anual190 ? 'Ocultar' : 'Calcular el 190')}
                </Button>
              </CardHeader>
              {anual190 && (
                <CardContent className="space-y-3">
                  <div className={`text-xs rounded px-3 py-2 border ${anual190.cuadra ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="liq-190-cuadre">
                    190: <b>{eur(anual190.totales?.retenciones)}</b> · 111 presentados: <b>{eur(anual190.totales?.retenciones_111_presentadas)}</b> · 111 calculados: {eur(anual190.totales?.retenciones_111_calculadas)} · diferencia {eur(anual190.totales?.diferencia)}
                    {anual190.cuadra ? ' · cuadra' : ' · ¡revisar!'}
                  </div>
                  {(anual190.avisos || []).map((a, i) => (
                    <p key={i} className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">{a.mensaje}</p>
                  ))}
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 border-b">
                        <tr className="text-slate-500">
                          <th className="text-left px-3 py-2">NIF</th>
                          <th className="text-left px-3 py-2">Perceptor</th>
                          <th className="text-left px-3 py-2">Clave</th>
                          <th className="text-left px-3 py-2">Subclave</th>
                          <th className="text-right px-3 py-2">Facturas</th>
                          <th className="text-right px-3 py-2">Percepción íntegra</th>
                          <th className="text-right px-3 py-2">Retenciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(anual190.perceptores || []).map((p, i) => (
                          <tr key={i} className="border-b last:border-0" data-testid={`liq-190-perceptor-${i}`}>
                            <td className={`px-3 py-1.5 font-mono ${p.nif ? '' : 'text-red-600'}`}>{p.nif || 'SIN NIF'}</td>
                            <td className="px-3 py-1.5">{p.nombre}</td>
                            <td className="px-3 py-1.5">{p.clave}</td>
                            <td className="px-3 py-1.5">{p.subclave}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{p.facturas}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{eur(p.percepcion_integra)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{eur(p.retenciones)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {(anual190.trimestral || []).map(t => (
                      <div key={t.trimestre} className="rounded border px-3 py-2 text-xs">
                        <p className="text-slate-500">{t.trimestre}º Trimestre</p>
                        <p className="tabular-nums">calculado {eur(t.calculado)}</p>
                        <p className="tabular-nums text-slate-500">{t.presentado == null ? 'sin presentar' : `presentado ${eur(t.presentado)}`}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>

            <Detalle titulo="Facturas recibidas con retención" filas={ret?.detalle?.retenciones} testId="liq-detalle-retenciones" />
          </TabsContent>

        </Tabs>
      )}
    </div>
  );
};

export default LiquidacionesTrimestrales;
